import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete,Button,DialogActions,DialogContent,DialogTitle,IconButton,Stack,TextField } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useMutation } from "@tanstack/react-query";
import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from '../../../helpers/matomo';
import Clients from "../../../service/Clients";
import Manages from "../../../service/Manages";
import queryClient from "../../../service/query";
import { NewManageTypeResponse,newManageType } from "../../../types/managements/types";
import GenericAcordonPlaces from "../../GenericAcordeonPlaces/GenericAcordeonPlaces";
import GenericAutoComplete from "../../GenericAutoComplete";

const customerService = new Clients();
const manageService = new Manages();

export default function NewMgmtModal({
  open,
  setOpen,
  results,
  selectedManage,
  setSelectedManage,
}: {
  open: boolean;
  setOpen: Function;
  results: NewManageTypeResponse;
  selectedManage: newManageType | null;
  setSelectedManage: Function;
}) {
  const { t } = useTranslation("translation");
  const [name, setName] = useState<string | null>(selectedManage?.nome || null);
  const [manages, setManages] = useState<string[] | null>(selectedManage?.gerentes || []);
  const [teams, setTeams] = useState<{ id: string; nome: string }[] | null>(selectedManage?.equipes || []);
  const [serviceTypes, setServiceTypes] = useState<{ id: string; nome: string; conjuntosAssociados: string[] }[] | null>(
    selectedManage?.tiposDeServico || [],
  );
  const [selectService, setSelectedService] = useState<string | null>(null);
  const [conjuntosArray, setConjuntosArray] = useState<string[]>([]);
  const [status, setStatus] = useState<string>("ativo");

  useEffect(() => {
    if (selectedManage) {
      setStatus(selectedManage.status ? "ativo" : "inativo");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    mutate,
    isPending: isLoadingPlaces,
    data,
  } = useMutation({
    mutationKey: ["GET_PLACES"],
    mutationFn: async (value: string) => {
      const token = await AttToken();
      if (token) {
        const response = await customerService.getAllWithSets(token, value);
        return response;
      }
    },
  });

  const { mutate: edit, isPending: isLoadingEdit } = useMutation({
    mutationKey: ["EDIT_MANAGE"],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          const token = await AttToken();
          if (token && selectedManage) {
            delete selectedManage?.dataDelecao;
            const response = await manageService.editV2(token, selectedManage);
            if (response.error || !response.gerencia) throw new Error("Error updating manage");
            return response;
          }
        },
        {
          pending: t("newManages.salvando"),
          success: t("newManages.salvo"),
          error: t("newManages.erroAoSalvar"),
        },
        {
          toastId: `edit-manage-${selectedManage?.id}`,
        },
      );
      return promise;
    },
    onSuccess: () => {
      trackEventMatomo('gestão de gerências', 'sucesso', 'edição', 'gerência atualizada');
      setOpen(false);
      queryClient.resetQueries({ queryKey: ["GET_MANAGESv2"] });
      setSelectedManage(null);
    },
    onError: () => {
      trackEventMatomo('gestão de gerências', 'erro', 'edição', 'falha ao atualizar gerência');
      setSelectedManage(null);
    },
  });

  const { mutate: create, isPending: isLoadingCreate } = useMutation({
    mutationKey: ["CREATE_MANAGE"],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          const token = await AttToken();
          if (token && selectedManage) {
            delete selectedManage?.dataDelecao;
            const response = await manageService.createV2(token, selectedManage);
            if (response.error || !response.gerencia) throw new Error("Error creating manage");
            return response;
          }
        },
        {
          pending: t("Managements.savingManage"),
          success: t("Managements.manageSaved"),
          error: t("Managements.errorOnSaveManage"),
        },
        { toastId: `create-manage-${selectedManage?.id}` },
      );
      return promise;
    },
    onSuccess: () => {
      trackEventMatomo('gestão de gerências', 'sucesso', 'criação', 'gerência criada');
      setOpen(false);
      queryClient.resetQueries({ queryKey: ["GET_MANAGESv2"] });
      setSelectedManage(null);
    },
    onError: () => {
      trackEventMatomo('gestão de gerências', 'erro', 'criação', 'falha ao criar gerência');
      setSelectedManage(null);
    },
  });

  const loading = isLoadingEdit || isLoadingCreate;

  useEffect(() => {
    let conjuntos = serviceTypes?.find((ele) => ele.id === selectService);
    if (conjuntos) {
      conjuntos.conjuntosAssociados = conjuntosArray;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conjuntosArray]);

  useEffect(() => {
    setSelectedManage({
      ...selectedManage,
      nome: name,
      dataAlteracao: selectedManage?.id ? new Date().getTime() : null,
      gerentes: manages,
      equipes: teams,
      tiposDeServico: serviceTypes,
      status: status === "ativo" ? true : false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, manages, teams, serviceTypes, status, conjuntosArray]);

  function handleManages(values: string[]) {
    let managesList: string[] = [];
    values?.forEach((ele) => {
      managesList.push(ele);
    });
    setManages(managesList);
  }

  function handleTeams(values: string[]) {
    const equipes = results[2].data?.filter((team: { id: string, nome: string }) => values.includes(team.id)) || [];
    setTeams(equipes.map((team: { id: string, nome: string }) => ({ id: team.id, nome: team.nome })));
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => {
        setOpen(false);
        setSelectedManage(null);
      }}
      scroll={"paper"}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        {`${selectedManage?.id ? t("edit") : t("create")}`} {t("Reports.reportsTable.management")}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          setOpen(false);
          setSelectedManage(null);
        }}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
          display: `${selectService ? "none" : ""}`,
        })}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        {selectService ? (
          <GenericAcordonPlaces isLoading={isLoadingPlaces} clients={data} conjuntosArray={conjuntosArray} setConjuntosArray={setConjuntosArray} />
        ) : (
          <Stack direction="column" justifyContent="flex-start" alignItems="center" spacing={2}>
            <TextField
              disabled={selectedManage?.geral}
              label={t("name")}
              fullWidth
              value={name || ""}
              onChange={(eve) => setName(eve.target.value)}
            />
            <GenericAutoComplete
              loading={false}
              options={
                results[1].data
                  ?.filter((user) => user.perfil === "Gestor" || user.perfil === "Gerente")
                  ?.map((user) => ({ label: user.nome, value: user.id, key: user })) ?? []
              }
              selectedValues={manages || []}
              onChange={(values) => {
                handleManages(values);
              }}
              label={t("teamManage.modal.manages")}
            />
            <GenericAutoComplete
              loading={false}
              options={results[2].data?.map((team: { id: string; nome: string }) => ({ value: team.id, label: team.nome })) || []}
              selectedValues={teams?.map((team) => team?.id) || []}
              onChange={(values) => {
                handleTeams(values);
              }}
              label={t("checklists.teams").replace("checklists", "teamManage.modal").replace(":", "")}
            />
            <GenericAutoComplete
              loading={false}
              options={
                results[3].data?.map((service) => {
                  return {
                    value: service.tipoId,
                    label: service.nome,
                    key: service,
                  };
                }) || []
              }
              selectedValues={serviceTypes?.map((service) => service.id) || []}
              onChange={(values) => {
                setServiceTypes(
                  values.map((ele) => {
                    return {
                      id: ele,
                      nome: results?.[3]?.data?.find((type) => type.tipoId === ele)?.nome || "",
                      conjuntosAssociados: selectedManage?.tiposDeServico?.find((elem) => elem.id === ele)?.conjuntosAssociados || [],
                    };
                  }),
                );
              }}
              label={t("teamManage.register.kindOfService")}
            />
            {serviceTypes && serviceTypes?.length > 0 && (
              <Autocomplete
                disabled={selectedManage?.geral}
                fullWidth
                disableClearable
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterSelectedOptions
                getOptionLabel={(option) => option.nome}
                options={
                  serviceTypes?.map((type) => ({
                    ...type,
                  })) ?? []
                }
                onChange={(_event, newValue) => {
                  setSelectedService(newValue.id);
                  setConjuntosArray(newValue?.conjuntosAssociados);
                  mutate(newValue.id);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" label={"Selecione o tipo para editar a hierarquia de conjuntos associados"} />
                )}
              />
            )}
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Status</InputLabel>
              <Select
                disabled={selectedManage?.geral}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={status}
                label="Status"
                onChange={(event) => setStatus(event.target.value)}>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!selectService ? (
          <Button
            onClick={() => {
              selectedManage?.geral ? setOpen(false) : !selectedManage?.id ? create() : edit();
            }}
            disabled={
              !name?.length ||
              !manages?.length ||
              !teams?.length ||
              !serviceTypes?.length ||
              (!selectedManage?.geral && serviceTypes?.some((ele) => !ele.conjuntosAssociados?.length)) ||
              loading
            }>
            {selectedManage?.geral ? t("close") : t("save")}
          </Button>
        ) : (
          <Button onClick={() => setSelectedService(null)} startIcon={<ArrowCircleLeftIcon color="primary" />}>
            {t("return")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
